import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import type { ComponentType } from 'react';

import { AppointmentDrawerView, AppointmentSchedulingDrawerConnected } from '@grow-therapy-team/seedling-components';

import { scheduledPatientInformationAtom } from '../schedule-preview/state';
import type { ScheduledPatientInformation } from '../schedule-preview/types';
import { ProviderDrawerState, drawerStateAtom, selectedClientInfoAtom } from '../state';

enum ScheduleEventType {
  APPOINTMENT = 'appointment',
  BUSY = 'busy',
  INVOICE = 'invoice',
}

function scheduledPatientInfoToEvent(info: ScheduledPatientInformation | undefined) {
  if (!info) return null;

  return {
    id: info.appointmentId,
    start: new Date(info.appointmentStartTime),
    end: new Date(info.appointmentEndTime),
    patientId: info.patientId,
    patientName: info.patientName,
    patientShortId: info.patientShortId,
    title: info.patientName,
    type: 'appointment' as ScheduleEventType,
    indefinite: info.indefinite,
    virtual: info.virtual,
    recurringGroupingId: info.recurringAppointmentGroupingId,
    weeksBetween: info.weeksBetween,
    numRecurrences: info.numRecurrences,
    appointmentShortId: info.appointmentShortId,
    eventId: '', // Not used in the new drawer; will be optional soon
    recurrenceRule: info.recurrenceRule ?? '',
    version: info.version ?? 1,
    numUpcoming: info.numRecurrences,
  };
}

export interface SchedulingAppointmentDrawerWrapperProps {
  is_add_appointment: boolean;
  AppointmentComponent?: ComponentType<React.ComponentProps<typeof AppointmentSchedulingDrawerConnected>>;
}

export function SchedulingAppointmentDrawerWrapper({
  is_add_appointment,
  AppointmentComponent = AppointmentSchedulingDrawerConnected,
}: SchedulingAppointmentDrawerWrapperProps) {
  const setDrawerState = useSetAtom(drawerStateAtom);

  const handleCloseDrawer = () => {
    setDrawerState(null);
  };

  const handlePreviousView = () => {
    setDrawerState(ProviderDrawerState.CLIENT_INFORMATION);
  };

  const { patientShortId, name: selectedClientName } = useAtomValue(selectedClientInfoAtom) ?? {};
  const scheduledPatientInformation = useAtomValue(scheduledPatientInformationAtom);
  const scheduledPatient =
    scheduledPatientInformation?.patientShortId === patientShortId ? scheduledPatientInformation : undefined;
  const patientName = scheduledPatient?.patientName ?? selectedClientName ?? '';

  const event = useMemo(() => scheduledPatientInfoToEvent(scheduledPatientInformation), [scheduledPatientInformation]);

  return (
    <AppointmentComponent
      drawerView={'edit-event' as AppointmentDrawerView}
      event={is_add_appointment ? null : event}
      isOpen={true}
      onClose={handleCloseDrawer}
      onPreviousView={handlePreviousView}
      patientShortId={patientShortId ?? ''}
      patientPreferredFullName={patientName}
      patientPronouns={scheduledPatient?.pronouns ?? ''}
      patientName={patientName}
      isInverse
    />
  );
}
